/**
 * Kanban Board Configuration for Incidents
 * PROJ-036: Uses Dynamics CRM hierarchical status model
 * - Columns based on statusReason (granular)
 * - Visual grouping by status (active/resolved/cancelled)
 */

import type { Incident, IncidentStatus, IncidentStatusReason } from '@/types/incidents';

export interface KanbanColumn {
  id: string;
  title: string;
  /** @deprecated Use statusReason instead */
  state: string;
  /** PROJ-036: Maps to incident.statusReason */
  statusReason: IncidentStatusReason;
  /** PROJ-036: Parent status for visual grouping */
  status: IncidentStatus;
  color: string; // Tailwind color class
  icon: string; // lucide-react icon name
  description: string;
}

/**
 * PROJ-036: Status group colors for visual distinction
 */
export const STATUS_GROUP_COLORS: Record<IncidentStatus, string> = {
  active: 'border-t-4 border-t-blue-500',
  resolved: 'border-t-4 border-t-green-500',
  cancelled: 'border-t-4 border-t-red-500',
};

/**
 * PROJ-036: Status group labels in Spanish
 */
export const STATUS_GROUP_LABELS: Record<IncidentStatus, string> = {
  active: 'Activo',
  resolved: 'Resuelto',
  cancelled: 'Cancelado',
};

/**
 * Kanban columns mapped to incident statusReason
 * PROJ-036: Organized by status groups (Active → Resolved → Cancelled)
 */
export const KANBAN_COLUMNS: KanbanColumn[] = [
  // ============================================================================
  // ACTIVE STATUS COLUMNS
  // ============================================================================
  {
    id: 'new',
    title: 'Nuevo',
    state: 'new', // Legacy compatibility
    statusReason: 'new',
    status: 'active',
    color: 'bg-blue-100 border-blue-500',
    icon: 'AlertCircle',
    description: 'Incidentes reportados recientemente',
  },
  {
    id: 'in_progress',
    title: 'En Progreso',
    state: 'assigned', // Legacy mapping
    statusReason: 'in_progress',
    status: 'active',
    color: 'bg-indigo-100 border-indigo-500',
    icon: 'User',
    description: 'Asignado y en trabajo activo',
  },
  {
    id: 'researching',
    title: 'Investigación',
    state: 'investigation', // Legacy mapping
    statusReason: 'researching',
    status: 'active',
    color: 'bg-purple-100 border-purple-500',
    icon: 'Search',
    description: 'Análisis de causa raíz en curso',
  },
  {
    id: 'containment',
    title: 'Contención',
    state: 'containment',
    statusReason: 'containment',
    status: 'active',
    color: 'bg-yellow-100 border-yellow-500',
    icon: 'ShieldAlert',
    description: 'Medidas para limitar el impacto',
  },
  {
    id: 'remediation',
    title: 'Remediación',
    state: 'remediation',
    statusReason: 'remediation',
    status: 'active',
    color: 'bg-orange-100 border-orange-500',
    icon: 'Wrench',
    description: 'Implementación de soluciones',
  },
  {
    id: 'on_hold',
    title: 'En Espera',
    state: 'on_hold',
    statusReason: 'on_hold',
    status: 'active',
    color: 'bg-slate-100 border-slate-500',
    icon: 'Pause',
    description: 'Pausado por dependencias externas',
  },
  {
    id: 'waiting_for_details',
    title: 'Esperando Detalles',
    state: 'on_hold', // Legacy mapping
    statusReason: 'waiting_for_details',
    status: 'active',
    color: 'bg-amber-100 border-amber-500',
    icon: 'MessageCircle',
    description: 'Aguardando información del reportador',
  },
  {
    id: 'post_incident',
    title: 'Post-Incidente',
    state: 'post_incident',
    statusReason: 'post_incident',
    status: 'active',
    color: 'bg-teal-100 border-teal-500',
    icon: 'FileText',
    description: 'Revisión y documentación final',
  },
  // ============================================================================
  // RESOLVED STATUS COLUMNS
  // ============================================================================
  {
    id: 'problem_solved',
    title: 'Problema Resuelto',
    state: 'resolved', // Legacy mapping
    statusReason: 'problem_solved',
    status: 'resolved',
    color: 'bg-green-100 border-green-500',
    icon: 'CheckCircle',
    description: 'Incidente solucionado completamente',
  },
  {
    id: 'information_provided',
    title: 'Información Provista',
    state: 'closed', // Legacy mapping
    statusReason: 'information_provided',
    status: 'resolved',
    color: 'bg-emerald-100 border-emerald-500',
    icon: 'Info',
    description: 'Resuelto proporcionando información',
  },
  // ============================================================================
  // CANCELLED STATUS COLUMNS
  // ============================================================================
  {
    id: 'cancelled',
    title: 'Cancelado',
    state: 'cancelled',
    statusReason: 'cancelled',
    status: 'cancelled',
    color: 'bg-red-100 border-red-500',
    icon: 'XCircle',
    description: 'Descartado o falso positivo',
  },
  {
    id: 'merged',
    title: 'Fusionado',
    state: 'cancelled', // Legacy mapping
    statusReason: 'merged',
    status: 'cancelled',
    color: 'bg-rose-100 border-rose-500',
    icon: 'GitMerge',
    description: 'Fusionado con otro incidente',
  },
];

/**
 * PROJ-036: Get column by statusReason (primary)
 */
export function getColumnByStatusReason(statusReason: string): KanbanColumn | undefined {
  return KANBAN_COLUMNS.find((col) => col.statusReason === statusReason);
}

/**
 * Get column by state (legacy compatibility)
 * @deprecated Use getColumnByStatusReason instead
 */
export function getColumnByState(state: string): KanbanColumn | undefined {
  // First try to match by statusReason (for new data)
  const byStatusReason = KANBAN_COLUMNS.find((col) => col.statusReason === state);
  if (byStatusReason) return byStatusReason;

  // Fall back to legacy state matching
  return KANBAN_COLUMNS.find((col) => col.state === state);
}

/**
 * Get column color by statusReason or state
 */
export function getColumnColor(statusReasonOrState: string): string {
  const column = getColumnByStatusReason(statusReasonOrState) || getColumnByState(statusReasonOrState);
  return column?.color || 'bg-gray-100 border-gray-500';
}

/**
 * PROJ-036: Map legacy state to statusReason
 */
export function mapLegacyStateToStatusReason(state: string): IncidentStatusReason {
  const legacyMapping: Record<string, IncidentStatusReason> = {
    'new': 'new',
    'assigned': 'in_progress',
    'investigation': 'researching',
    'containment': 'containment',
    'remediation': 'remediation',
    'on_hold': 'on_hold',
    'post_incident': 'post_incident',
    'resolved': 'problem_solved',
    'closed': 'problem_solved',
    'cancelled': 'cancelled',
    // Extra legacy mappings
    'open': 'new',
    'in_progress': 'in_progress',
  };
  return legacyMapping[state] || 'new';
}

/**
 * PROJ-036: Group incidents by statusReason for Kanban display
 */
export function groupIncidentsByStatusReason(incidents: Incident[]): Record<string, Incident[]> {
  const grouped: Record<string, Incident[]> = {};

  // Initialize all columns
  KANBAN_COLUMNS.forEach((column) => {
    grouped[column.statusReason] = [];
  });

  // Group incidents
  incidents.forEach((incident) => {
    // Use statusReason if available, otherwise map from legacy state
    let statusReason = incident.statusReason || mapLegacyStateToStatusReason(incident.state || 'new');

    if (!grouped[statusReason]) {
      grouped[statusReason] = [];
    }
    grouped[statusReason].push(incident);
  });

  return grouped;
}

/**
 * Group incidents by state for Kanban display
 * @deprecated Use groupIncidentsByStatusReason instead
 */
export function groupIncidentsByState(incidents: Incident[]): Record<string, Incident[]> {
  // Redirect to new function for backward compatibility
  return groupIncidentsByStatusReason(incidents);
}

/**
 * PROJ-036: Group columns by status for visual grouping
 */
export function getColumnsByStatus(status: IncidentStatus): KanbanColumn[] {
  return KANBAN_COLUMNS.filter((col) => col.status === status);
}

/**
 * Sort incidents within a column
 */
export function sortIncidentsByPriority(incidents: Incident[]): Incident[] {
  const severityOrder: Record<string, number> = {
    critical: 1,
    high: 2,
    medium: 3,
    low: 4,
  };

  return [...incidents].sort((a, b) => {
    // Sort by severity first
    const severityA = severityOrder[a.severity] || 999;
    const severityB = severityOrder[b.severity] || 999;

    if (severityA !== severityB) {
      return severityA - severityB;
    }

    // Then by created date (newest first)
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });
}

/**
 * PROJ-036: Check if incident is in a read-only status (resolved or cancelled)
 */
export function isIncidentReadOnly(incident: Incident): boolean {
  return incident.status === 'resolved' || incident.status === 'cancelled';
}
